import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LogIn, Mail, Lock, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

const LoginForm: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [pendingApproval, setPendingApproval] = useState(false);
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!email || !password) {
            toast.error('Por favor completa todos los campos');
            return;
        }

        setLoading(true);
        try {
            await login(email, password);
            // Usar replace para evitar que el usuario pueda volver al login con el botón atrás
            window.location.replace('/');
        } catch (error: any) {
            console.error('Error de login:', error);

            // Verificar si es error de pendiente de aprobación
            if (error.message.includes('pendiente de aprobación') || error.message.includes('PENDING_APPROVAL')) {
                setPendingApproval(true);
                setLoading(false);
                return;
            }

            let message = 'Error al iniciar sesión';

            if (error.message.includes('user-not-found')) {
                message = 'Usuario no encontrado';
            } else if (error.message.includes('wrong-password')) {
                message = 'Contraseña incorrecta';
            } else if (error.message.includes('invalid-email')) {
                message = 'Email inválido';
            } else if (error.message.includes('too-many-requests')) {
                message = 'Demasiados intentos. Intenta más tarde';
            } else if (error.message.includes('invalid-credential')) {
                message = 'Email o contraseña incorrectos';
            }

            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    // Mostrar pantalla de pendiente de aprobación
    if (pendingApproval) {
        return (
            <div className="text-center py-8 space-y-6">
                <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto">
                    <Clock className="w-10 h-10 text-amber-400" />
                </div>
                <div>
                    <h3 className="text-2xl font-bold text-white mb-2">
                        Cuenta Pendiente de Aprobación
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed max-w-sm mx-auto">
                        Tu cuenta ha sido creada pero el administrador aún no la ha aprobado.
                        Recibirás acceso cuando el administrador revise y apruebe tu solicitud.
                    </p>
                </div>
                <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-4 max-w-sm mx-auto">
                    <p className="text-amber-400 text-sm">
                        💡 <strong>Tip:</strong> Contacta con el administrador de tu gimnasio para agilizar el proceso de aprobación.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => setPendingApproval(false)}
                    className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
                >
                    Volver a intentar
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            <div>
                <h3 className="text-xl font-semibold text-white mb-2">
                    Accede a tu cuenta
                </h3>
                <p className="text-gray-500 text-sm mb-6">
                    Introduce tus credenciales para continuar
                </p>

                <div className="space-y-4">
                    <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                            Email
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Mail className="h-5 w-5 text-gray-500" />
                            </div>
                            <input
                                id="email"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                placeholder="tu@email.com"
                            />
                        </div>
                    </div>

                    <div>
                        <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
                            Contraseña
                        </label>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Lock className="h-5 w-5 text-gray-500" />
                            </div>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full pl-10 pr-3 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all"
                                placeholder="••••••••"
                            />
                        </div>
                    </div>

                    <div className="flex items-center justify-end">
                        <Link
                            to="/forgot-password"
                            className="text-sm text-amber-400 hover:text-amber-300 transition-colors"
                        >
                            ¿Olvidaste tu contraseña?
                        </Link>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold rounded-xl shadow-lg shadow-amber-500/25 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                        {loading ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                Iniciando sesión...
                            </>
                        ) : (
                            <>
                                <LogIn className="w-5 h-5" />
                                Iniciar Sesión
                            </>
                        )}
                    </button>
                </div>
            </div>
        </form>
    );
};

export default LoginForm;
